/**
 * Opaque, client-minted identifiers for the identity module: session ids
 * (`ses_`), page-view ids (`pgv_`), producer event ids (`pev_`) and form
 * instance / embed nonces. Crockford-base32 ULIDs (10 time chars + 16 random)
 * so they sort by creation time and match milla's `^<prefix>_[A-Za-z0-9]{8,64}$`
 * patterns. None of these is a visitor identity; they are per-tab, per-page
 * or per-event correlation handles only.
 * @module lib/identity/ids
 */

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function randomBytes(length) {
  const bytes = new Uint8Array(length);
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      crypto.getRandomValues(bytes);
      return bytes;
    }
  } catch (_) {
    // fall through to the non-crypto fallback
  }
  for (let index = 0; index < length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  return bytes;
}

function ulid(now = Date.now()) {
  let time = now;
  const timeChars = new Array(10);
  for (let index = 9; index >= 0; index -= 1) {
    timeChars[index] = CROCKFORD[time % 32];
    time = Math.floor(time / 32);
  }
  const random = randomBytes(16);
  let randomChars = '';
  for (let index = 0; index < 16; index += 1) randomChars += CROCKFORD[random[index] & 31];
  return timeChars.join('') + randomChars;
}

const makeId = (prefix) => `${prefix}_${ulid()}`;

const newSessionId = () => makeId('ses');
const newPageViewId = () => makeId('pgv');
const newProducerEventId = () => makeId('pev');
const newFormInstanceId = () => makeId('frm');
const newEmbedNonce = () => makeId('emn');

function toHex(bytes) {
  let out = '';
  for (let index = 0; index < bytes.length; index += 1) out += bytes[index].toString(16).padStart(2, '0');
  return out;
}

/**
 * `sha256:<hex>` of a string using WebCrypto; null when unavailable (plain
 * http origins, very old browsers). Callers treat null as "cannot bind".
 */
async function sha256Ref(value) {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle || typeof TextEncoder === 'undefined') return null;
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(value)));
    return `sha256:${toHex(new Uint8Array(digest))}`;
  } catch (_) {
    return null;
  }
}

module.exports = {
  makeId,
  newEmbedNonce,
  newFormInstanceId,
  newPageViewId,
  newProducerEventId,
  newSessionId,
  randomBytes,
  sha256Ref,
  ulid
};
